import React, { useState } from 'react';
import { CreditCard, Calendar, FileText, ChevronDown, ChevronUp, CheckCircle, Download, AlertTriangle, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const SubscriptionSubscribed = ({ subscription }) => {
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  // Datos simulados para la demostración
  const currentPlan = {
    name: subscription?.plan_name || 'Plus',
    renewalDate: subscription?.current_period_end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    features: [
      'Todo lo de Inicio más:',
      'Operaciones ilimitadas',
      'Plantillas personalizadas',
      'Reportes avanzados',
      'Roles y permisos'
    ],
    price: 69,
    billingCycle: 'annual'
  };

  const upgradePlan = {
    name: 'Pro',
    features: [
      'Impresoras en comodato',
      'Acceso a nuevas funciones',
      'Más de 15 usuarios',
      'Soporte prioritario 24/7'
    ],
    price: 149,
    billingCycle: 'annual'
  };

  const paymentMethod = {
    type: 'credit_card',
    last4: '4242',
    brand: 'Visa',
    expMonth: 12,
    expYear: 2025
  };

  const paymentHistory = [
    {
      id: 'pay_123456',
      date: new Date(2023, 11, 15),
      amount: 69,
      status: 'completed',
      invoiceUrl: '#'
    },
    {
      id: 'pay_123455',
      date: new Date(2023, 10, 15),
      amount: 69,
      status: 'completed',
      invoiceUrl: '#'
    },
    {
      id: 'pay_123454',
      date: new Date(2023, 9, 15),
      amount: 69,
      status: 'completed',
      invoiceUrl: '#'
    },
    {
      id: 'pay_123453',
      date: new Date(2023, 8, 15),
      amount: 69,
      status: 'completed',
      invoiceUrl: '#'
    }
  ];

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'd MMMM yyyy', { locale: es });
  };

  const handleChangePlan = () => {
    alert('En una implementación real, aquí se abriría un modal para cambiar de plan.');
  };

  const handleCancelSubscription = () => {
    alert('En una implementación real, aquí se abriría un modal para confirmar la cancelación.');
  };

  const handleUpdatePaymentMethod = () => {
    alert('En una implementación real, aquí se redigiría a Mercado Pago para actualizar el método de pago.');
  };

  const handleWhatsAppContact = () => {
    // Número y mensaje para WhatsApp (cambiar por el número real)
    const phoneNumber = '5491112345678';
    const message = `Hola, tengo una consulta sobre mi suscripción actual (Plan ${currentPlan.name}).`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Estado de suscripción actual */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-800 rounded-full flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Plan {currentPlan.name} Activo
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Próxima renovación: {formatDate(currentPlan.renewalDate)}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <button
            onClick={() => setShowPlanDetails(!showPlanDetails)}
            className="flex items-center justify-between w-full text-left py-2"
          >
            <span className="font-medium text-gray-700 dark:text-gray-300">
              Detalles del plan actual
            </span>
            {showPlanDetails ? (
              <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            )}
          </button>

          {showPlanDetails && (
            <div className="mt-2 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <div className="flex justify-between mb-3">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                    {currentPlan.name}
                  </span>
                  <span className="text-gray-900 dark:text-white font-bold">
                    {formatPrice(currentPlan.price)}/{currentPlan.billingCycle === 'monthly' ? 'mes' : 'año'}
                  </span>
                </div>
                <ul className="space-y-2">
                  {currentPlan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary-500 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Información de facturación */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Información de facturación
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Método de pago */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-gray-700 dark:text-gray-300 font-medium">Método de pago</h4>
              <button
                onClick={handleUpdatePaymentMethod}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Actualizar
              </button>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
              <CreditCard className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-gray-800 dark:text-gray-200">
                  {paymentMethod.brand} terminada en {paymentMethod.last4}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Vence: {paymentMethod.expMonth}/{paymentMethod.expYear}
                </p>
              </div>
            </div>
          </div>

          {/* Ciclo de facturación */}
          <div className="space-y-3">
            <h4 className="text-gray-700 dark:text-gray-300 font-medium">Ciclo de facturación</h4>
            <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-gray-800 dark:text-gray-200">
                  {currentPlan.billingCycle === 'monthly' ? 'Mensual' : 'Anual'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Próximo cargo: {formatDate(currentPlan.renewalDate)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Historial de pagos */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Historial de pagos
          </h3>
          <button
            onClick={() => setShowAllHistory(!showAllHistory)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {showAllHistory ? 'Ver menos' : 'Ver todo'}
          </button>
        </div>

        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Factura
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {(showAllHistory ? paymentHistory : paymentHistory.slice(0, 2)).map((payment) => (
                <tr key={payment.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {formatDate(payment.date)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {formatPrice(payment.amount)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      payment.status === 'completed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {payment.status === 'completed' ? 'Completado' : 'Pendiente'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <a
                      href={payment.invoiceUrl}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 inline-flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                      <span>PDF</span>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Opciones de plan */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
          <button
            onClick={() => setShowUpgradeOptions(!showUpgradeOptions)}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Opciones de plan
            </h3>
            {showUpgradeOptions ? (
              <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            )}
          </button>
        </div>

        {showUpgradeOptions && (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-gray-200">
                    Actualizar a {upgradePlan.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Obtén más funcionalidades para tu negocio
                  </p>
                </div>
                <span className="text-gray-900 dark:text-white font-bold">
                  {formatPrice(upgradePlan.price)}/{upgradePlan.billingCycle === 'monthly' ? 'mes' : 'año'}
                </span>
              </div>
              <ul className="space-y-2 mb-4">
                {upgradePlan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-primary-500 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleChangePlan}
                className="w-full py-2 bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-white font-medium rounded-lg"
              >
                Actualizar ahora
              </button>
            </div>

            <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCancelSubscription}
                className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 text-sm font-medium flex items-center gap-2 mx-auto"
              >
                <AlertTriangle className="w-4 h-4" />
                Cancelar suscripción
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Tu suscripción continuará activa hasta el final del período facturado.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Botón de WhatsApp y soporte */}
      <div className="mb-6 flex justify-center">
        <button 
          onClick={handleWhatsAppContact}
          className="inline-flex items-center gap-2 text-white bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg font-medium"
        >
          <MessageCircle className="w-5 h-5" />
          Contactar soporte por WhatsApp
        </button>
      </div>

      {/* Información de seguridad y MercadoPago */}
      <div className="text-center">
        <div className="flex justify-center gap-4 mb-4 items-center bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg inline-flex">
          <img 
            src="https://www.mercadopago.com/org-img/Manual/LogoMP/LogoMP.png" 
            alt="Mercado Pago" 
            className="h-6" 
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">Pagos seguros procesados por Mercado Pago</span>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSubscribed; 